import {MeshBounds} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshBounds';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshRange, MeshRangeCardinality} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {R1C1Range, R1C1RangeIndexes} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/R1C1';
import {MeshLocationUtils} from '@mintdata-wsp/common/util/MeshLocationUtils';

export class MeshUtils
{
    public static getCellRefRange(startColLetter:string, startRowLetter:string, endColLetter:string, endRowLetter:string):MeshRange
    {
        let startCol:number = MeshLocationUtils.getColumnIndexByLetter(startColLetter);
        let startRow:number = MeshLocationUtils.getRowIndexByLetter(startRowLetter);
        let endCol:number = MeshLocationUtils.getColumnIndexByLetter(endColLetter);
        let endRow:number = MeshLocationUtils.getRowIndexByLetter(endRowLetter);

        if( startCol != null && endCol != null && startCol > endCol )
            [startCol, endCol] = [endCol, startCol];

        if( startRow == null && endRow == null )
            startRow = 0;
        else if( startRow == null && endRow != null )
        {
            startRow = endRow;
            endRow = null;
        }

        const startLocation:MeshLocation = new MeshLocation(startCol, startRow);
        const endLocation:MeshLocation = new MeshLocation(endCol, endRow);

        return new MeshRange(startLocation, endLocation);
    }

    public static getCellRefRangeFromR1C1(r1c1Range:R1C1Range, currentColumnIndex:number, currentRowIndex:number):MeshRange
    {
        if( !r1c1Range )
            return null;

        const rangeIndexes:R1C1RangeIndexes = r1c1Range.getNormalizedRange(
            currentColumnIndex,
            currentRowIndex
        ).getIndexes(
            currentColumnIndex,
            currentRowIndex
        );

        return new MeshRange(
            new MeshLocation(
                rangeIndexes.startIndexes
                    ? rangeIndexes.startIndexes.colIndex
                    : null,
                rangeIndexes.startIndexes
                    ? rangeIndexes.startIndexes.rowIndex
                    : null
            ),
            new MeshLocation(
                rangeIndexes.endIndexes
                    ? rangeIndexes.endIndexes.colIndex
                    : null,
                rangeIndexes.endIndexes
                    ? rangeIndexes.endIndexes.rowIndex
                    : null
            )
        );
    }

    public static getRangeBounds(from:MeshLocation, to:MeshLocation, meshState:MeshState):MeshBounds
    {
        if( !meshState || !from || !to )
            return null;

        let startRow = from.rowIndex;
        let startCol = from.colIndex;

        let endRow = to.rowIndex;
        let endCol = to.colIndex;

        if( startCol == null && startRow == null && endCol == null && endRow == null )
            return null;

        if( endCol == null && (startRow != null || endRow != null) )
            endCol = meshState.colWidths.length - 1;
        else if( endCol == null && startCol != null )
            endCol = startCol;

        if( endRow == null && (startCol != null || endCol != null) )
            endRow = meshState.rowHeights.length - 1;
        else if( endRow == null && startRow != null )
            endRow = startRow;

        if( startCol == null && startRow != null )
            startCol = 0;

        if( startRow == null && startCol != null )
            startRow = 0;

        return MeshUtils.getRangeBounds2(meshState, startRow, startCol, endRow, endCol);
    }

    public static getRangeBounds2(meshState:MeshState, startRow:number, startCol:number, endRow:number, endCol:number):MeshBounds
    {
        if( !meshState )
            return null;

        if( startRow < 0 || endRow >= meshState.rowHeights.length ||
            startCol < 0 || endCol >= meshState.colWidths.length )
        {
            return null;
        }

        const rowHeightSumFn = (startRow, endRow) =>
        {
            let result = 0;

            for( let i = startRow; i <= endRow; i++ )
            {
                result += meshState.details.resizingRowIndex == i
                    ? meshState.details.resizingRowHeight
                    : meshState.rowHeights[i];
            }

            return result;
        };

        const colWidthSumFn = (startCol, endCol) =>
        {
            let result = 0;

            for( let i = startCol; i <= endCol; i++ )
            {
                result += meshState.details.resizingColIndex == i
                    ? meshState.details.resizingColWidth
                    : meshState.colWidths[i];
            }

            return result;
        };

        const offsetX = colWidthSumFn(0, startCol - 1);
        const offsetY = rowHeightSumFn(0, startRow - 1);
        const width = colWidthSumFn(startCol, endCol);
        const height = rowHeightSumFn(startRow, endRow);

        const {alignedScrollLeft, alignedScrollTop} = meshState;

        return MeshBounds.from(
            (startCol > 0 ? -1 : 0) + offsetX - alignedScrollLeft,
            (startRow > 0 ? -1 : 0) + offsetY - alignedScrollTop,
            (startCol > 0 ? 1 : 0) + width,
            (startRow > 0 ? 1 : 0) + height
        );
    }

    public static getCellAtCursor(clientX:number,
                                  clientY:number,
                                  meshState:MeshState,
                                  cornerWidth:number,
                                  cornerHeight:number):MeshLocation
    {
        if( !meshState || clientX < 0 || clientY < 0
            || (clientX >= meshState.geometricSize.width + cornerWidth)
            || (clientY >= meshState.geometricSize.height + cornerHeight) )
            return null;

        const {visibleStartRowIndex, visibleStartColIndex} = meshState;
        let offsetXToVisible:number = 0;
        let offsetYToVisible:number = 0;

        for( let i = 0; i < visibleStartColIndex; i++ )
        {
            offsetXToVisible += meshState.colWidths[i];
        }

        for( let i = 0; i < visibleStartRowIndex; i++ )
        {
            offsetYToVisible += meshState.rowHeights[i];
        }

        let column:number = null;
        let row:number = null;

        if( clientX >= offsetXToVisible + cornerWidth )
        {
            let width:number = offsetXToVisible + cornerWidth;

            for( let i = visibleStartColIndex; i < meshState.colWidths.length; i++ )
            {
                width += meshState.colWidths[i];

                if( clientX <= width )
                {
                    column = i;
                    break;
                }
            }

            if (column === null)
                column = meshState.colWidths.length - 1;
        }

        if( clientY >= offsetYToVisible + cornerHeight )
        {
            let height:number = offsetYToVisible + cornerHeight;

            for( let i = visibleStartRowIndex; i < meshState.rowHeights.length; i++ )
            {
                height += meshState.rowHeights[i];

                if( clientY <= height )
                {
                    row = i;
                    break;
                }
            }

            if (row === null)
                row = meshState.rowHeights.length - 1;
        }

        return column != null || row != null ? new MeshLocation(column, row) : null;
    }

    public static eachCellInRange(range:MeshRange, numRows:number, numCols:number, iteratee:(cellKey:string) => void)
    {
        const {from, to} = range;

        const cardinality = MeshUtils.getRangeCardinality(range);

        switch( cardinality )
        {
            case MeshRangeCardinality.FullTable:
                for( let rowIndex = 0; rowIndex < numRows; rowIndex++ )
                {
                    for( let colIndex = 0; colIndex < numCols; colIndex++ )
                    {
                        iteratee(new MeshLocation(colIndex, rowIndex).asKey());
                    }
                }
                break;
            case MeshRangeCardinality.FullColumns:
                for( let rowIndex = 0; rowIndex < numRows; rowIndex++ )
                {
                    for( let colIndex = from.colIndex; colIndex <= to.colIndex; colIndex++ )
                    {
                        iteratee(new MeshLocation(colIndex, rowIndex).asKey());
                    }
                }
                break;
            case MeshRangeCardinality.FullRows:
                for( let rowIndex = from.rowIndex; rowIndex <= to.rowIndex; rowIndex++ )
                {
                    for( let colIndex = 0; colIndex < numCols; colIndex++ )
                    {
                        iteratee(new MeshLocation(colIndex, rowIndex).asKey());
                    }
                }
                break;
            case MeshRangeCardinality.Cells:
                for( let rowIndex = from.rowIndex; rowIndex <= to.rowIndex; rowIndex++ )
                {
                    for( let colIndex = from.colIndex; colIndex <= to.colIndex; colIndex++ )
                    {
                        iteratee(new MeshLocation(colIndex, rowIndex).asKey());
                    }
                }
                break;
            default:
                break;
        }
    }

    public static someCellInRange(range:MeshRange, numRows:number, numCols:number, iteratee:(cellKey:string) => boolean):boolean
    {
        const {from, to} = range;

        const cardinality = MeshUtils.getRangeCardinality(range);

        switch( cardinality )
        {
            case MeshRangeCardinality.FullTable:
                for( let rowIndex = 0; rowIndex < numRows; rowIndex++ )
                {
                    for( let colIndex = 0; colIndex < numCols; colIndex++ )
                    {
                        if( iteratee(new MeshLocation(colIndex, rowIndex).asKey()) )
                        {
                            return true;
                        }
                    }
                }
                break;
            case MeshRangeCardinality.FullColumns:
                for( let rowIndex = 0; rowIndex < numRows; rowIndex++ )
                {
                    for( let colIndex = from.colIndex; colIndex <= to.colIndex; colIndex++ )
                    {
                        if( iteratee(new MeshLocation(colIndex, rowIndex).asKey()) )
                        {
                            return true;
                        }
                    }
                }
                break;
            case MeshRangeCardinality.FullRows:
                for( let rowIndex = from.rowIndex; rowIndex <= to.rowIndex; rowIndex++ )
                {
                    for( let colIndex = 0; colIndex < numCols; colIndex++ )
                    {
                        if( iteratee(new MeshLocation(colIndex, rowIndex).asKey()) )
                        {
                            return true;
                        }
                    }
                }
                break;
            case MeshRangeCardinality.Cells:
                for( let rowIndex = from.rowIndex; rowIndex <= to.rowIndex; rowIndex++ )
                {
                    for( let colIndex = from.colIndex; colIndex <= to.colIndex; colIndex++ )
                    {
                        if( iteratee(new MeshLocation(colIndex, rowIndex).asKey()) )
                        {
                            return true;
                        }
                    }
                }
                break;
            default:
                break;
        }

        return false;
    }

    public static getRangeCardinality(range:MeshRange):MeshRangeCardinality
    {
        const {from} = range;

        if( from.colIndex != null && from.rowIndex != null )
        {
            return MeshRangeCardinality.Cells;
        }
        else if( from.colIndex != null )
        {
            return MeshRangeCardinality.FullColumns;
        }
        else if( from.rowIndex != null )
        {
            return MeshRangeCardinality.FullRows;
        }
        else
        {
            return MeshRangeCardinality.FullTable;
        }
    }

    public static sheetNameToRefSheetName(sheetName:string):string
    {
        let refSheetName = sheetName.replace(/'/g, '\'\'');
            
        if( refSheetName.includes(' ') || refSheetName.includes('\'\'') )
            refSheetName = `'${refSheetName}'`;

        return refSheetName;
    }

    public static refSheetNameToSheetName(refSheetName:string):string
    {
        const sheetName = refSheetName;

        if( sheetName.startsWith('\'') && sheetName.endsWith('\'') )
            return sheetName.slice(1, -1).replace(/''/g, '\'');

        return sheetName;
    }
}
